import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Eye, HeartHandshake, TrendingUp, ArrowUpRight, Award } from "lucide-react";
import { stats, companyFacts, branches, awards } from "@/data/content";
import { JourneyNetwork } from "@/components/JourneyNetwork";
import useSeo from "@/hooks/useSeo";

const pillars = [
    {
        icon: Target,
        title: "Mission",
        body: "We commit ourselves to offer quality services with safety, security, flexibility and innovation. We work in an environment with a perfect blend of Planning and Execution to meet the challenges of change and competition.",
    },
    {
        icon: Eye,
        title: "Vision",
        body: "To be a quality-driven global service provider and be rewarded for the value and innovation we provide across diverse markets and sectors.",
    },
    {
        icon: HeartHandshake,
        title: "Values",
        body: "We value our manpower and train them to work with team spirit. Quality, safety, security, flexibility and innovation are the principles that shape every shift.",
    },
    {
        icon: TrendingUp,
        title: "Edge",
        body: "Founded in 2015 and led by Mr. Chirag Shridhar, Managing Director. Trusted by 100+ leading OEMs and Tier-1 suppliers — from Yamaha and Honda to Tata Motors, Mahindra, Denso, Hitachi Astemo and Carrier.",
    },
];

export default function About() {
    useSeo({
        title: "About — Since 2015, led by Mr. Chirag Shridhar",
        description:
            "Beeta Logistics Private Limited, founded in 2015 by Mr. Chirag Shridhar (Managing Director). 28 branches, 90,000+ sqft warehousing, 10,000+ operations, 100+ customers including Yamaha, Honda, Tata Motors, Carrier and Zepto.",
        path: "/about",
    });
    return (
        <div data-testid="about-page" className="bg-white">
            {/* HEADER */}
            <section className="relative pt-16 pb-24 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-7">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5">
                                About Beeta · Since 2015
                            </div>
                            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95]">
                                A logistics company
                                <br />
                                that <span className="text-blue-600">operates like an engineer.</span>
                            </h1>
                            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                                Beeta Logistics is one of the leading logistics organisations in
                                India. Our mission is to set a new benchmark for logistics —
                                integrating solutions, optimising the supply chain to its core
                                and creating Win-Win outcomes for every customer we serve.
                            </p>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="lg:col-span-5 relative"
                        >
                            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-xl ring-1 ring-slate-900/5">
                                <img
                                    src="/assets/about-side.webp"
                                    alt="Inside a Beeta Logistics warehouse"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent" />
                                {/* Floating accent badge */}
                                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-xl rounded-2xl p-4 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                                        <span className="font-display font-black text-blue-600 text-lg">B</span>
                                    </div>
                                    <div className="leading-tight">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                                            Operating standard
                                        </div>
                                        <div className="font-display font-bold text-slate-900 text-sm">
                                            Quality · Safety · Innovation
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative yellow accent */}
                            <div className="hidden lg:block absolute -bottom-6 -right-6 w-32 h-32 rounded-3xl bg-brand-yellow/30 -z-10" />
                            <div className="hidden lg:block absolute -top-4 -left-4 w-20 h-20 rounded-2xl bg-blue-600/15 -z-10" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* STORY */}
            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="rounded-3xl overflow-hidden aspect-[4/5]"
                        >
                            <img
                                src="/assets/about-hero.webp"
                                alt="Beeta Logistics dock at golden hour"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            The story
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight mb-6">
                            Founded in 2015, built for the long run.
                        </h2>
                        <div className="space-y-5 text-slate-700 leading-relaxed text-lg">
                            <p>
                                Beeta Logistics Private Limited was incorporated in 2015 by
                                <span className="font-semibold text-slate-900"> Mr. Chirag Shridhar</span>,
                                who continues to lead the company as Managing Director. The
                                focus from day one: integrate logistics solutions and
                                optimise supply chain operations to the core, with quality,
                                safety, flexibility and innovation as the operating
                                principles.
                            </p>
                            <p>
                                A decade in, the network runs across 20+ industrial cities in
                                India — from Gurgaon-Manesar-Bawal in the north, Chakan
                                (Pune) and Nashik in the west, to Sriperumbudur (Chennai),
                                Sricity, Bengaluru and Hosur in the south. Six-plus
                                warehouses totalling 90,000+ sq. ft, 10,000+ operations
                                delivered across owned and vendor lanes, and 175+ permanent
                                and contractual people across the operation.
                            </p>
                            <p>
                                When we say{" "}
                                <span className="font-semibold text-slate-900">&ldquo;transportation beyond compare&rdquo;</span>
                                , it isn&apos;t a tagline. It&apos;s the benchmark we measure
                                ourselves against every shift.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="py-24 sm:py-32 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {pillars.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="p-8 rounded-2xl bg-white border border-slate-200 hover:shadow-xl transition-all"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                                        <Icon className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h3 className="font-display text-2xl font-black text-slate-900 tracking-tight mb-3">
                                        {p.title}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed">{p.body}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* COMPANY OVERVIEW TABLE */}
            <section className="py-24 sm:py-32 bg-white border-t border-slate-200" data-testid="overview-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Company Overview
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                            The facts,
                            <br />
                            on the record.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            Beeta Logistics Private Limited — registered, audited and
                            operating openly across India since 2015.
                        </p>
                    </div>

                    <div className="lg:col-span-8">
                        <dl className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            {[
                                ["Company name", companyFacts.name],
                                ["Year of incorporation", companyFacts.incorporated],
                                ["Managing Director", `${companyFacts.founder} · ${companyFacts.founderTitle}`],
                                ["Corporate office", companyFacts.headOffice],
                                ["GSTIN", companyFacts.gstin],
                                ["Operations", "10,000+ deliveries across owned & vendor lanes"],
                                ["Manpower", `${companyFacts.manpower.permanent} permanent · ${companyFacts.manpower.contractual} contractual`],
                                ["Warehouse network", companyFacts.warehouseSpace],
                                ["Customers", companyFacts.customers],
                                ["FY 25–26 revenue", `${companyFacts.turnover} · target ${companyFacts.growthTarget}`],
                            ].map(([k, v]) => (
                                <div
                                    key={k}
                                    className="grid sm:grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 transition-colors"
                                >
                                    <dt className="sm:col-span-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                                        {k}
                                    </dt>
                                    <dd className="sm:col-span-8 text-slate-800 font-medium">
                                        {v}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </section>

            {/* BRANCHES */}
            <section className="py-20 sm:py-24 bg-slate-50 border-t border-slate-200" data-testid="branches-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Branch Network
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                            28 branches.
                            <br />
                            One operating standard.
                        </h2>
                    </div>

                    <div className="mt-12 flex flex-wrap gap-3">
                        {branches.map((b, i) => (
                            <motion.div
                                key={b}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 bg-white hover:border-blue-600 hover:shadow-md transition-all"
                                data-testid={`branch-pill-${i}`}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" />
                                <span className="font-semibold text-slate-800 text-sm">{b}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MILESTONES — Journey Network */}
            <section className="bg-white border-t border-slate-200" data-testid="milestones-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32">
                    <div className="max-w-3xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Our Journey
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                            From one route
                            <br />
                            to a connected network.
                        </h2>
                    </div>
                </div>
                <JourneyNetwork />
            </section>

            {/* AWARDS */}
            <section className="py-24 sm:py-32 bg-slate-50 border-t border-slate-200" data-testid="awards-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-16">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Awards & Recognition
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                            Trusted by the customers we serve.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            The recognition that matters most comes from our customers — year after year.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {awards.map((a, i) => (
                            <motion.div
                                key={`${a.year}-${a.title}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="p-7 rounded-2xl border border-slate-200 bg-white hover:shadow-xl transition-all relative overflow-hidden"
                                data-testid={`award-card-${i}`}
                            >
                                <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-brand-yellow/10 blur-2xl" />
                                <div className="relative">
                                    {a.image && (
                                        <div className="mb-5 -mx-7 -mt-7 h-40 overflow-hidden bg-slate-100">
                                            <img
                                                src={a.image}
                                                alt={a.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center">
                                            <Award className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-brand-yellow px-2.5 py-1 rounded-full bg-slate-900">
                                            {a.year}
                                        </div>
                                    </div>
                                    <h3 className="font-display text-lg font-bold text-slate-900 leading-tight mb-2">
                                        {a.title}
                                    </h3>
                                    <div className="text-sm text-slate-500">
                                        by <span className="font-semibold text-slate-800">{a.by}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-24 sm:py-32 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter max-w-3xl">
                        Operating at scale, every single day.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-4 gap-6">
                        {stats.map((s) => (
                            <div key={s.label} className="p-8 rounded-2xl border border-slate-800 bg-slate-800/30">
                                <div className="font-display text-6xl font-black tracking-tighter">
                                    {s.value}
                                    <span className="text-brand-yellow">{s.suffix}</span>
                                </div>
                                <div className="mt-3 text-sm font-semibold text-slate-400 uppercase tracking-wider">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-14 flex flex-wrap gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                        >
                            Talk to our team <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-slate-700 text-white font-semibold hover:bg-slate-800 transition-colors"
                        >
                            Explore services
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
